// const Toast = React.lazy(() => import("primereact/toast"));
// Styles
// Import Flag Icons Set
import "flag-icon-css/css/flag-icon.min.css";
// Import Font Awesome Icons Set
import "../node_modules/@fortawesome/fontawesome-free/css/all.min.css";
// Import Simple Line Icons Set
import "simple-line-icons/css/simple-line-icons.css";
// Import Main styles for this application
import "../scss/style.scss";
// App shell layout and global overrides (must load so .app / .main / .container-fluid apply on all routes)
import "./styles/overrides.css";
// Temp fix for reactstrap
import "../scss/core/_dropdown-menu-right.scss";
// Import Primereact Set
import "primereact/resources/themes/nova/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
// Portal overrides for PrimeReact (must load after nova theme)
import "./styles/primereact-overrides.css";
// Re-declare simple-line / primeicons faces without EOT+SVG (see file).
import "./styles/iconFontFaces.css";

import { MsalContext, MsalProvider } from "@azure/msal-react";
import * as Sentry from "@sentry/browser";
import dayjs from "dayjs";
import { Toast } from "primereact/toast";
import React, { lazy, Suspense } from "react";
import CookieConsent from "react-cookie-consent";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import { Button, Popover, PopoverBody, PopoverHeader } from "reactstrap";

import { alertActions } from "./actions";
import { userActions } from "./actions";
import componentLoader from "./bigFiles/componentLoader";
import { DeployAssetErrorBoundary } from "./components/DeployAssetErrorBoundary";
import { DeploySuspenseFallback } from "./components/DeploySuspenseFallback";
import helpData from "./components/Sidebar/HelpData.json";
// Containers
import FullHomePage from "./containers/Full/";
import { history as appHistory } from "./helpers";
import { setGlobalClientId, setSessionClientId } from "./helpers/client";
import {
  checkMaintenanceWithRetry,
  CONNECTIVITY_BANNER_BODY,
  isConnectivityBannerState,
  isMaintenancePageState,
} from "./helpers/maintenance";
import { maybeClearPostAuthGraceOnHashChange } from "./helpers/postAuthGrace";
import { apiService } from "./services";
// Views
const LoginPage = lazy(() =>
  componentLoader(() => import("./views/Login/")),
);
const WaitingLogin = lazy(() =>
  componentLoader(() => import("./views/Login/WaitingLogin")),
);

const HoldingPage = lazy(() =>
  componentLoader(() => import("./views/Holding/")),
);

const Help = lazy(() =>
  componentLoader(
    () => import("./views/Login/Help/The-Portal---explainer-document-_1_"),
  ),
);

const Maintenance = lazy(() =>
  componentLoader(() => import("./views/Admin/Maintenance")),
);

import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";

const GA_MEASUREMENT_ID = "G-02N2DL0947";

// Clarity consent: stop + cookie cleanup, and reopen banner
function deleteCookie(name, options = {}) {
  const parts = [`${name}=`, "Max-Age=0", "path=/", "SameSite=Lax"];
  if (options.domain) parts.push(`domain=${options.domain}`);
  if (options.secure) parts.push("Secure");
  document.cookie = parts.join("; ");
}

function deleteCookieEverywhere(name) {
  try {
    const host = window.location.hostname;
    deleteCookie(name);
    deleteCookie(name, { domain: host });
    if (host.includes(".")) {
      const parts = host.split(".");
      if (parts.length >= 2) {
        const etld = `.${parts.slice(-2).join(".")}`;
        deleteCookie(name, { domain: etld });
      }
    }
  } catch (e) {
    /* noop */
  }
}

function cleanupClarityCookies() {
  const names = ["_clck", "_clsk", "CLID", "ANONCHK", "MR", "MUID", "SM"];
  names.forEach((n) => deleteCookieEverywhere(n));
}

function stopAndCleanupClarity() {
  try {
    if (typeof window.clarity === "function") {
      try {
        // New API: explicit denial
        window.clarity("consentv2", {
          ad_Storage: "denied",
          analytics_Storage: "denied",
        });
      } catch (e) {
        /* noop */
      }
      try {
        // Back-compat cookie purge
        window.clarity("consent", false);
      } catch (e) {
        /* noop */
      }
      try {
        window.clarity("stop");
      } catch (e) {
        /* noop */
      }
    }
  } finally {
    cleanupClarityCookies();
  }
}

function removeClarityScripts() {
  try {
    const tags = document.querySelectorAll(
      'script[src*="clarity.ms/tag/"]',
    );
    tags.forEach((tag) => {
      if (tag && tag.parentNode) {
        tag.parentNode.removeChild(tag);
      }
    });
  } catch (e) {
    /* noop */
  }
}

function injectClarity(code) {
  try {
    // Allow reinjection by clearing stub state
    window.__clarityDisabled = false;
    if (window.clarity && window.clarity.__stub === true) {
      try {
        window.clarity = undefined;
      } catch (e) {
        /* noop */
      }
    }
    (function (c, l, a, r, i, t, y) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", code);
  } catch (e) {
    /* noop */
  }
}

function cleanupGoogleAnalyticsCookies() {
  try {
    const cookiePairs = document.cookie.split(";");
    const names = cookiePairs
      .map((c) => c.split("=")[0]?.trim())
      .filter(Boolean);
    names.forEach((name) => {
      if (
        name === "_ga" ||
        name === "_gid" ||
        name === "_gat" ||
        name.startsWith("_ga_") ||
        name.startsWith("_gat_") ||
        name.startsWith("_gac_") ||
        name.startsWith("_gcl_")
      ) {
        deleteCookieEverywhere(name);
      }
    });
  } catch (e) {
    /* noop */
  }
}

function disableGoogleAnalytics() {
  try {
    window["ga-disable-" + GA_MEASUREMENT_ID] = true;
    if (typeof window.gtag === "function") {
      try {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      } catch (e) {
        /* noop */
      }
    }
  } catch (e) {
    /* noop */
  }
}

function removeGoogleAnalyticsScripts() {
  try {
    const tags = document.querySelectorAll(
      'script[src*="googletagmanager.com/gtag/js"], script[src*="google-analytics.com/analytics.js"]',
    );
    tags.forEach((tag) => {
      if (tag && tag.parentNode) {
        tag.parentNode.removeChild(tag);
      }
    });
  } catch (e) {
    /* noop */
  }
}

/**
 * Snapshot for debugging 2FA banner vs build-inlined REACT_APP_2FA_DATE.
 * In the browser console run: __log2faBannerContext()
 */
function get2faBannerDebugSnapshot(user, isBannerVisibleState) {
  const raw = process.env.REACT_APP_2FA_DATE;
  const cutoff = dayjs(raw);
  const now = dayjs(new Date());
  return {
    hint: "Paste this object when debugging 2FA banner / .env mismatch.",
    state_isBannerVisible: isBannerVisibleState,
    inlined_REACT_APP_2FA_DATE: raw,
    cutoff_valid: cutoff.isValid(),
    cutoff_formatted_DD_MMMM_YYYY: cutoff.isValid()
      ? cutoff.format("DD MMMM YYYY")
      : "(invalid date)",
    now_iso: now.toISOString(),
    dayjs_now_isBefore_cutoff: now.isBefore(raw),
    user_present: Boolean(user),
    user_companyId: user?.companyId,
    user_identityProvider: user?.identityProvider,
    gate_companyId_truthy: Boolean(user?.companyId),
    gate_identityProvider_is_null: user?.identityProvider === null,
    computed_bannerShouldShow:
      Boolean(user) &&
      Boolean(user?.companyId) &&
      user?.identityProvider === null &&
      now.isBefore(raw),
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
  };
}

class App extends React.Component {
  constructor(props) {
    super(props);
    setSessionClientId();
    this.state = {
      popoverOpen: false,
      cookieChange: false,
      show: false,
      updates: [],
      page: "",
      helpData: false,
      size: Object.values(helpData).length,
      isBannerVisible:
        !!this?.props?.user &&
        this.props.user.companyId &&
        this.props.user.identityProvider === null &&
        dayjs(new Date()).isBefore(process.env.REACT_APP_2FA_DATE),
    };
    const { dispatch } = this.props;
    this.showToast = this.showToast.bind(this);
    this.clearToast = this.clearToast.bind(this);
    this.setHelpDataAll = this.setHelpDataAll.bind(this);
    this.setHelpData = this.setHelpData.bind(this);

    appHistory.listen((location, _action) => {
      this.setState({ page: location.location.hash });
      this.setHelpData();

      // if (process.env.REACT_APP_ENVIRONMENT === "prod") {
      console.log("location.hash-", location.location.hash);
      //ReactGA.set({ page: location.path }); // Update the user's current page //Not sure I need that yet...
      // }

      // clear alert upon location change
      this.props.dispatch(alertActions.clear());
      Sentry.addBreadcrumb({
        category: "pageChange",
        message: "Page: " + location.location.hash,
        level: "info",
      });
      const hashStr = String(location?.location?.hash || "");
      maybeClearPostAuthGraceOnHashChange(hashStr);
      // Added: detect MSAL auth handoff (redirect/silent/popup) query in hash
      const isAuthHandoff =
        /([#&](code|id_token|access_token|state)=)/i.test(
          decodeURIComponent(hashStr),
        );
      if (
        !hashStr.includes("maintenance") &&
        !hashStr.includes("login") &&
        // Suppress maintenance checks during explicit logout navigation
        !hashStr.includes("logout") &&
        // Added: Avoid maintenance redirect while MSAL is parsing auth hash
        !isAuthHandoff
      ) {
        checkMaintenanceWithRetry(this.props.dispatch);
      }

      if (
        location &&
        location.hash &&
        location.hash.includes("error=quotaExceeded")
      ) {
        this.toast.show({
          severity: "error",
          summary: "Session storage is full",
          detail: "User information was too long to be set in storage.",
          delay: 10000,
        });
      }
    });
    this.toggle = this.toggle.bind(this);
    this.handleConnectivityRetry = this.handleConnectivityRetry.bind(this);
  }

  handleConnectivityRetry() {
    checkMaintenanceWithRetry(this.props.dispatch);
  }

  static contextType = MsalContext;

  setHelpDataAll(bool) {
    this.setState({ helpDataAll: bool });
    //if helpDataAll was set to true, we want all the data for all pages
    //else if false, we jsut want to re-run our code that grabs and sets the data for the specific page we're on
    if (bool) {
      this.setState({ helpData: helpData });
    } else {
      this.setHelpData();
    }
  }

  handleBannerDismiss() {
    this.setState({ isBannerVisible: false });
  }

  setHelpData() {
    //check whether we have help information available for the current page
    //we might have different help info for external and internal users, so we need to check this as well- if there's no audience it's for everyone
    let count = 0;
    Object.values(helpData).map((obj, i) => {
      if (
        window.location.href.includes(obj.url) &&
        (!obj.audience ||
          (this.props.user &&
            this.props.user.companyId == 2 &&
            obj.audience == "Internal") ||
          (this.props.user &&
            this.props.user.companyId != 2 &&
            obj.audience == "External"))
      ) {
        this.setState({ helpData: [obj] });
      } else {
        //increment the count that will help us figure out if there were no matches to the current page
        count++;
      }
      //if the size (length of helpData from json) is the same count we've incrmeneted, and we're at the end of the loop, there's no matching data, so set it to false to be clear
      if (this.state.size == i + 1 && this.state.size == count) {
        this.setState({ helpData: false });
      }
    });
  }

  // Add componentDidUpdate method
  componentDidUpdate(prevProps, _prevState) {
    // Check if user data just became available or changed
    if (prevProps.user !== this.props.user) {
      const bannerShouldShow =
        !!this.props.user &&
        this.props.user.companyId &&
        this.props.user.identityProvider === null &&
        dayjs(new Date()).isBefore(process.env.REACT_APP_2FA_DATE);

      // Only update if the calculated value is different from current state
      if (bannerShouldShow !== this.state.isBannerVisible) {
        this.setState({ isBannerVisible: bannerShouldShow });
      }
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[2FA banner debug] user changed",
          get2faBannerDebugSnapshot(this.props.user, bannerShouldShow),
        );
      }
      // ADD THIS: Set header when user becomes available
      if (this.props.user) {
        setGlobalClientId(this.props.user);
      }
    }
  }
  componentDidMount() {
    window.__log2faBannerContext = () => {
      const snap = get2faBannerDebugSnapshot(
        this.props.user,
        this.state.isBannerVisible,
      );
      console.log("[2FA banner debug]", snap);
      return snap;
    };

    if (process.env.NODE_ENV === "development" && this.props.user) {
      console.log(
        "[2FA banner debug] mount",
        get2faBannerDebugSnapshot(
          this.props.user,
          this.state.isBannerVisible,
        ),
      );
    }

    // Allow users to reopen the cookie banner and revoke consent at any time
    // Note:
    // - We intentionally attach this as a global function on `window` so that
    //   other components (e.g. the Header user menu) can trigger it without
    //   prop-drilling or setting up context/Redux wiring.
    // - Defining this function does not execute anything on mount. It only
    //   runs when another part of the app calls `window.reopenCookieBanner()`.
    // - When invoked, it clears the CookieConsent choice, stops Microsoft
    //   Clarity and removes its cookies immediately, then toggles local state
    //   to force a re-render so the <CookieConsent> component shows again.
    // When user proceeds with auth/signup without choosing cookies, treat as deny
    // but do NOT set CookieConsent cookie so the banner stays visible until they choose.
    window.ensureAnalyticsDeniedWithoutSettingCookie = () => {
      stopAndCleanupClarity();
      removeClarityScripts();
      disableGoogleAnalytics();
      cleanupGoogleAnalyticsCookies();
      removeGoogleAnalyticsScripts();
      try {
        const noop = function () {};
        noop.__stub = true;
        window.clarity = noop;
        window.__clarityDisabled = true;
      } catch (e) {
        /* noop */
      }
    };

    window.reopenCookieBanner = () => {
      // Clear prior choice so the banner is shown again
      deleteCookieEverywhere("CookieConsent");
      // Stop tracking and clear Clarity cookies immediately
      stopAndCleanupClarity();
      removeClarityScripts();
      // Stop and clear Google Analytics immediately
      disableGoogleAnalytics();
      cleanupGoogleAnalyticsCookies();
      removeGoogleAnalyticsScripts();
      // Stub clarity to prevent any further calls until accept
      try {
        const noop = function () {};
        noop.__stub = true;
        window.clarity = noop;
        window.__clarityDisabled = true;
      } catch (e) {
        /* noop */
      }
      // Trigger re-render so <CookieConsent> reappears
      this.setState((prev) => ({ cookieChange: !prev.cookieChange }));
    };

    this.setHelpData();

    // Skip maintenance check on login page or during logout - let login/logout handle it
    if (
      window.location.hash.includes("login") ||
      window.location.hash.includes("logout")
    ) {
      return;
    }

    // Set client ID if user is logged in
    if (this.props.user) {
      setGlobalClientId(this.props.user);
    }

    // Simple maintenance check - no timeout, let it fail fast
    // Skip during MSAL auth hash handoff to avoid race with MSAL parsing
    if (
      !/(#|&)(code|id_token|access_token|state)=/i.test(
        decodeURIComponent(window.location.hash || ""),
      )
    ) {
      checkMaintenanceWithRetry(this.props.dispatch);
    }

    //the login will fail if it's not a secure origin, so get user to switch to https
    if (process.env.REACT_APP_ENVIRONMENT === "prod") {
      if (location.protocol && location.protocol !== "https:") {
        this.toast.show({
          severity: "error",
          summary: "Error",
          detail: `There is a problem with the protocol used: '${location.protocol}'. Please change to the 'https:' protocol before logging in.`,
          sticky: true,
        });
      }
    }

    //if cookie has been set to consent to the cookies, initiate google analytics and windows clarity (we add script tag later to prevent clarity trying to recird users even when they decline cookies)
    if (document.cookie.includes("CookieConsent=true")) {
      // Re-enable GA before init in case it was disabled on a prior decline
      try {
        window["ga-disable-" + GA_MEASUREMENT_ID] = false;
        if (typeof window.gtag === "function") {
          window.gtag("consent", "update", {
            analytics_storage: "granted",
          });
        }
      } catch (e) {
        /* noop */
      }
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gaOptions: {
          cookieFlags: "SameSite=None;Secure",
        },
      });
      try {
        ReactGA.send({
          hitType: "pageview",
          page:
            window.location.pathname +
            window.location.search +
            window.location.hash,
        });
      } catch (e) {
        /* noop */
      }
      let code =
        process.env.REACT_APP_ENVIRONMENT === "prod"
          ? "dgb6b8288v"
          : "dgb5zj6r6a";
      injectClarity(code);
      // Use consentv2: ads denied, analytics granted
      try {
        window.clarity("consentv2", {
          ad_Storage: "denied",
          analytics_Storage: "granted",
        });
      } catch (e) {
        /* noop */
      }
    }
  }

  showToast(severity, summary, detail, sticky, content) {
    if (this.toast) {
      this.toast.show({
        severity: severity,
        summary: summary,
        detail: detail,
        sticky: sticky,
        content: content,
      });
    }
  }

  clearToast() {
    this.toast.clear();
  }

  showDialog() {
    this.setState({ show: true });
  }

  toggle() {
    this.setState((prevState) => {
      return { popoverOpen: !prevState.popoverOpen };
    });
  }
  render() {
    const { alert } = this.props;
    const isMaintenanceHash =
      typeof window !== "undefined" &&
      window.location &&
      typeof window.location.hash === "string" &&
      window.location.hash.includes("maintenance");
    const showMaintenancePage =
      isMaintenancePageState(this.props.maintenanceMessage) ||
      isMaintenanceHash;
    const showConnectivityBanner =
      !showMaintenancePage &&
      isConnectivityBannerState(this.props.maintenanceMessage);
    const connectivityBannerBody =
      (this.props.maintenanceMessage &&
        this.props.maintenanceMessage.body) ||
      CONNECTIVITY_BANNER_BODY;
    // No global MSAL spinner gate; transitional UI handled by routes/components

    const isHelpRoute =
      typeof window !== "undefined" &&
      typeof window.location.hash === "string" &&
      window.location.hash.startsWith("#/help");
    //we set the id based on the location becasue we only want to show the scrollbar on help page
    return (
      <MsalProvider instance={this.props.pca}>
        <div id={isHelpRoute ? "help" : "div"}>
          <Toast ref={(el) => (this.toast = el)} />
          {process.env.REACT_APP_ENVIRONMENT === "prod" && (
            <Helmet>
              <title>My AJW</title>
            </Helmet>
          )}
          {showConnectivityBanner && (
            <div
              role="alert"
              className="alert alert-warning mb-0"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                position: "sticky",
                top: 0,
                zIndex: 1001,
                borderRadius: 0,
              }}
            >
              <span>{connectivityBannerBody}</span>
              <Button
                color="warning"
                size="sm"
                outline
                onClick={this.handleConnectivityRetry}
              >
                Try again
              </Button>
            </div>
          )}
          {alert.message && alert.type && (
            <div
              className={`alert ${alert.type}`}
              style={{
                display: "flex",
                justifyContent: "center",
                position: "absolute",
                opacity: 0.7,
              }}
            >
              {alert.message}
            </div>
          )}
          {process.env.REACT_APP_ENVIRONMENT === "prod" ? (
            <CookieConsent
              key={this.state.cookieChange ? "cc-1" : "cc-0"}
              //debug={true}
              onAccept={() => {
                // Re-enable GA before init in case it was disabled on a prior decline
                try {
                  window["ga-disable-" + GA_MEASUREMENT_ID] = false;
                  if (typeof window.gtag === "function") {
                    window.gtag("consent", "update", {
                      analytics_storage: "granted",
                    });
                  }
                } catch (e) {
                  /* noop */
                }
                ReactGA.initialize(GA_MEASUREMENT_ID, {
                  gaOptions: {
                    cookieFlags: "SameSite=None;Secure",
                  },
                });
                try {
                  ReactGA.send({
                    hitType: "pageview",
                    page:
                      window.location.pathname +
                      window.location.search +
                      window.location.hash,
                  });
                } catch (e) {
                  /* noop */
                }
                let code =
                  process.env.REACT_APP_ENVIRONMENT === "prod"
                    ? "dgb6b8288v"
                    : "dgb5zj6r6a";
                injectClarity(code);
                try {
                  window.clarity("consentv2", {
                    ad_Storage: "denied",
                    analytics_Storage: "granted",
                  });
                } catch (e) {
                  /* noop */
                }
                //rerender so that user can login
                this.setState((prevState) => {
                  return {
                    cookieChange: !prevState.cookieChange,
                  };
                });
              }}
              enableDeclineButton
              onDecline={() => {
                stopAndCleanupClarity();
                disableGoogleAnalytics();
                cleanupGoogleAnalyticsCookies();
                removeGoogleAnalyticsScripts();
                this.setState((prevState) => {
                  return {
                    cookieChange: !prevState.cookieChange,
                  };
                });
              }}
              sameSite="lax"
            >
              <div style={{ marginLeft: "200px", lineHeight: "20px" }}>
                To ensure we give you the best experience on our website we
                use Cookies. You can change your cookie settings at any
                time. However, if you agree without changing your settings
                we will presume you are happy to receive all cookies on the
                AJW website.
                <Button
                  aria-label="app-Button-Cookie"
                  id="PopoverCookie"
                  type="button"
                  style={{ marginLeft: "2px" }}
                >
                  More Info
                </Button>
                <Popover
                  placement="bottom"
                  isOpen={this.state.popoverOpen}
                  target="PopoverCookie"
                  toggle={this.toggle}
                >
                  <PopoverHeader>Cookie Policy</PopoverHeader>
                  <PopoverBody>
                    Our website makes use of Cookies (Cookies are small
                    files stored on your computer) for the following
                    purposes:
                    <ul>
                      <li>
                        Enabling a service to recognise your computer so
                        you don't have to give the same information several
                        times during one task
                      </li>
                      <li>
                        Measuring how many people are using services, so
                        they can be made easier to use and there's enough
                        capacity to ensure they are fast
                      </li>
                    </ul>
                    For more information on how to manage and delete
                    Cookies please visit the{" "}
                    <a
                      href="https://www.aboutcookies.org/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      About Cookies
                    </a>{" "}
                    <br></br>
                    website.<br></br>
                    <br></br> Google analytics (_ga, _gid, _gat): These
                    cookies are used to collect information about how
                    visitors use our site. We use the information to
                    compile reports and to help us improve the site. The
                    cookies collect information in an anonymous form,
                    including the number of visitors to the site, where
                    visitors have come to the site from and the pages they
                    visited. We only place these cookies on your computer
                    if you consent to us putting them there. <br></br>
                    <br></br> Microsoft Clarity (_clck, _clsk, CLID,
                    ANONCHK, MR, MUID, SM: a list of these cookies and
                    their purpose can be found in the{" "}
                    <a
                      href="https://docs.microsoft.com/en-us/clarity/cookie-list"
                      target="_blank"
                      rel="noreferrer"
                    >
                      cookie list
                    </a>{" "}
                    section of their documentation): These cookies are used
                    to better understand our users' needs and to optimize
                    this service and experience. Microsoft Clarity is a
                    technology service that helps us better understand our
                    users' experience (e.g. how much time they spend on
                    which pages, which links they choose to click, what
                    users do and don't like, etc.) and this enables us to
                    build and maintain our service with user feedback.
                    Microsoft Clarity uses cookies and other technologies
                    to collect data on our users' behavior and their
                    devices. This includes a device's IP address (processed
                    during your session and stored in a de-identified
                    form), device screen size, device type (unique device
                    identifiers), browser information, geographic location
                    (country only), and the preferred language used to
                    display our website. Microsoft Clarity stores this
                    information on our behalf in a pseudonymized user
                    profile. Microsoft Clarity does not sell any of the
                    data collected on our behalf. If cookies are declined,
                    Micorosoft Clarity will not be initialised. For further
                    details, please see the 'Privacy' section of the{" "}
                    <a
                      href="https://docs.microsoft.com/en-us/clarity/faq"
                      target="_blank"
                      rel="noreferrer"
                    >
                      FAQs
                    </a>
                    . <br></br>
                    <br></br> App Version (appVersion and any partial URLs
                    such as /orders/sales): These cookies are used to track
                    which app updates have been seen and acknowledged
                    across the different pages of the portal to minimise
                    how many notification users receive.
                    <br></br> <br></br>Accept cookies (CookieConsent): This
                    cookie is placed on your computer if you have accepted
                    cookies from the site. If you refuse cookies we don't
                    set this one, which is why you'll be reminded every
                    time.
                  </PopoverBody>
                </Popover>
              </div>
            </CookieConsent>
          ) : (
            <CookieConsent
              key={this.state.cookieChange ? "cc-1" : "cc-0"}
              //debug={true}
              onAccept={() => {
                let code =
                  process.env.REACT_APP_ENVIRONMENT === "prod"
                    ? "dgb6b8288v"
                    : "dgb5zj6r6a";
                injectClarity(code);
                try {
                  window.clarity("consentv2", {
                    ad_Storage: "denied",
                    analytics_Storage: "granted",
                  });
                } catch (e) {
                  /* noop */
                }
                // Re-enable GA before init in case it was disabled on a prior decline
                try {
                  window["ga-disable-" + GA_MEASUREMENT_ID] = false;
                  if (typeof window.gtag === "function") {
                    window.gtag("consent", "update", {
                      analytics_storage: "granted",
                    });
                  }
                } catch (e) {
                  /* noop */
                }
                ReactGA.initialize(GA_MEASUREMENT_ID, {
                  gaOptions: {
                    cookieFlags: "SameSite=None;Secure",
                  },
                });
                try {
                  ReactGA.send({
                    hitType: "pageview",
                    page:
                      window.location.pathname +
                      window.location.search +
                      window.location.hash,
                  });
                } catch (e) {
                  /* noop */
                }
                this.setState((prevState) => {
                  return {
                    cookieChange: !prevState.cookieChange,
                  };
                });
              }}
              enableDeclineButton
              onDecline={() => {
                stopAndCleanupClarity();
                disableGoogleAnalytics();
                cleanupGoogleAnalyticsCookies();
                removeGoogleAnalyticsScripts();
                this.setState((prevState) => {
                  return {
                    cookieChange: !prevState.cookieChange,
                  };
                });
              }}
              sameSite="lax"
            >
              <div style={{ marginLeft: "200px", lineHeight: "20px" }}>
                To ensure we give you the best experience on our website we
                use Cookies. You can change your cookie settings at any
                time. However, if you agree without changing your settings
                we will presume you are happy to receive all cookies on the
                AJW website.
                <Button
                  aria-label="app-Button-Cookie"
                  id="PopoverCookie"
                  type="button"
                  style={{ marginLeft: "2px" }}
                >
                  More Info
                </Button>
                <Popover
                  placement="bottom"
                  isOpen={this.state.popoverOpen}
                  target="PopoverCookie"
                  toggle={this.toggle}
                >
                  <PopoverHeader>Cookie Policy</PopoverHeader>
                  <PopoverBody>
                    Our website makes use of Cookies (Cookies are small
                    files stored on your computer) for the following
                    purposes:
                    <ul>
                      <li>
                        Enabling a service to recognise your computer so
                        you don't have to give the same information several
                        times during one task
                      </li>
                      <li>
                        Measuring how many people are using services, so
                        they can be made easier to use and there's enough
                        capacity to ensure they are fast
                      </li>
                    </ul>
                    For more information on how to manage and delete
                    Cookies please visit the{" "}
                    <a
                      href="https://www.aboutcookies.org/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      About Cookies
                    </a>{" "}
                    <br></br>
                    website.<br></br>
                    <br></br> Microsoft Clarity (_clck, _clsk, CLID,
                    ANONCHK, MR, MUID, SM: a list of these cookies and
                    their purpose can be found in the{" "}
                    <a
                      href="https://docs.microsoft.com/en-us/clarity/cookie-list"
                      target="_blank"
                      rel="noreferrer"
                    >
                      cookie list
                    </a>{" "}
                    section of their documentation): These cookies are used
                    to better understand our users' needs and to optimize
                    this service and experience. Microsoft Clarity is a
                    technology service that helps us better understand our
                    users' experience (e.g. how much time they spend on
                    which pages, which links they choose to click, what
                    users do and don't like, etc.) and this enables us to
                    build and maintain our service with user feedback.
                    Microsoft Clarity uses cookies and other technologies
                    to collect data on our users' behavior and their
                    devices. This includes a device's IP address (processed
                    during your session and stored in a de-identified
                    form), device screen size, device type (unique device
                    identifiers), browser information, geographic location
                    (country only), and the preferred language used to
                    display our website. Microsoft Clarity stores this
                    information on our behalf in a pseudonymized user
                    profile. Microsoft Clarity does not sell any of the
                    data collected on our behalf. If cookies are declined,
                    Micorosoft Clarity will not be initialised. For further
                    details, please see the 'Privacy' section of the{" "}
                    <a
                      href="https://docs.microsoft.com/en-us/clarity/faq"
                      target="_blank"
                      rel="noreferrer"
                    >
                      FAQs
                    </a>
                    . <br></br>
                    <br></br> Google analytics (_ga, _gid, _gat): These
                    cookies are used to collect information about how
                    visitors use our site. We use the information to
                    compile reports and to help us improve the site. The
                    cookies collect information in an anonymous form,
                    including the number of visitors to the site, where
                    visitors have come to the site from and the pages they
                    visited. We only place these cookies on your computer
                    if you consent to us putting them there. <br></br>
                    <br></br> App Version (appVersion and any partial URLs
                    such as /orders/sales): These cookies are used to track
                    which app updates have been seen and acknowledged
                    across the different pages of the portal to minimise
                    how many notification users receive.
                    <br></br> <br></br>Accept cookies (CookieConsent): This
                    cookie is placed on your computer if you have accepted
                    cookies from the site. If you refuse cookies we don't
                    set this one, which is why you'll be reminded every
                    time.
                  </PopoverBody>
                </Popover>
              </div>
            </CookieConsent>
          )}
          <DeployAssetErrorBoundary
            reportExhausted={() => {
              Sentry.captureMessage("Chunk load failure", {
                level: "warning",
                tags: {
                  resourceType: "chunk",
                  recoveryStatus: "exhausted",
                  errorType: "chunk_load_failure",
                },
              });
            }}
          >
            <Suspense
              fallback={
                <DeploySuspenseFallback
                  reportExhausted={() => {
                    Sentry.captureMessage("Chunk load failure", {
                      level: "warning",
                      tags: {
                        resourceType: "chunk",
                        recoveryStatus: "exhausted",
                        errorType: "suspense_watchdog",
                      },
                    });
                  }}
                />
              }
            >
              {showMaintenancePage ? (
                <Switch>
                  <Route
                    path="/maintenance"
                    name="Maintenance"
                    component={Maintenance}
                  />
                  <Redirect from="*" to="/maintenance" />
                </Switch>
              ) : (
                <Switch>
                  <Route
                    path="/loggingIn"
                    name="Waiting Login"
                    component={WaitingLogin}
                  />
                  <Route
                    path="/login"
                    render={() => (
                      <LoginPage
                        showToast={this.showToast}
                        clearToast={this.clearToast}
                      />
                    )}
                  />
                  <Route
                    path="/holding"
                    render={(props) => (
                      <HoldingPage
                        {...props}
                        handleBannerDismiss={this.handleBannerDismiss.bind(
                          this,
                        )}
                        showDialog={this.showDialog.bind(this)}
                        isBannerVisible={this.state.isBannerVisible}
                        setHelpDataAll={this.setHelpDataAll}
                      />
                    )}
                  />
                  <Route path="/help" name="Help" component={Help} />
                  <Route
                    path="/"
                    render={(props) => (
                      <FullHomePage
                        {...props}
                        handleBannerDismiss={this.handleBannerDismiss.bind(
                          this,
                        )}
                        isBannerVisible={this.state.isBannerVisible}
                        showDialog={this.showDialog.bind(this)}
                        helpData={this.state.helpData}
                        setHelpDataAll={this.setHelpDataAll}
                        helpDataAll={this.state.helpDataAll}
                        page={this.state.page}
                      />
                    )}
                  />
                </Switch>
              )}
            </Suspense>
          </DeployAssetErrorBoundary>
        </div>
      </MsalProvider>
    );
  }
}

function mapStateToProps(state) {
  const { alert } = state;
  const { authentication } = state;
  const { user } = authentication;
  const { maintenanceMessage } = state;
  console.log("user", user, alert);
  return { alert, user, maintenanceMessage, authentication };
}
const connectedApp = connect(mapStateToProps)(App);
export default connectedApp;
